import React, { useEffect, useMemo, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Search, X, Menu as MenuIcon, Clock, Phone, User, ShoppingCart, QrCode, CalendarClock, Bike, CheckCircle2, XCircle, ChefHat, PackageCheck, Check, Sparkles, CreditCard, Banknote, Smartphone, RefreshCw, ArrowLeft, MessageSquare, AlertCircle, ShieldCheck, Hash, Printer, Receipt, Building2, Copy } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { CBETransferVerifier } from './CBETransferVerifier';
import { useOrders, Order, OrderStatus, PaymentStatus, OrderMethod, statusConfig, paymentConfig, methodConfig, paymentMethodLabel, timeSincePlaced, orderBranchId } from './OrdersContext';
import { useBranches, isKdsEnabled } from './BranchesContext';
import { useOutlets } from './OutletsContext';
// Import for the suggested edits, possibly unused but imported for completeness
import { Filter, MoreVertical, ChevronRight, AlertTriangle, Trash2 } from 'lucide-react';
const nextStatus: Record<OrderStatus, OrderStatus | null> = {
  new: 'preparing',
  preparing: 'ready',
  ready: 'completed',
  completed: null,
  cancelled: null
};
export function OrdersPage() {
  const {
    orders,
    setStatus,
    setPaymentStatus,
    updateOrder,
    recentlyVerifiedId,
    markPrinted
  } = useOrders();
  type StatusFilter = OrderStatus | 'all';
  type MethodFilter = OrderMethod | 'all';
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('new');
  const [methodFilter, setMethodFilter] = useState<MethodFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [cancelModalOrderId, setCancelModalOrderId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const {
    branches,
    branchId
  } = useBranches();
  const {
    getOutletsForBranch
  } = useOutlets();
  const activeBranch = branches.find(b => b.id === branchId) || branches[0];
  const branchOutlets = getOutletsForBranch(activeBranch.id);
  const kdsEnabled = isKdsEnabled(activeBranch);
  // Hide waiter tab orders that are still open — they belong to the waiter
  // terminal until the tab is closed and handed off to the cashier.
  const visibleOrders = useMemo(() => orders.filter(o => !o.isOpenTab), [orders]);
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: visibleOrders.length,
      new: 0,
      preparing: 0,
      ready: 0,
      completed: 0,
      cancelled: 0
    };
    visibleOrders.forEach(o => {
      counts[o.status] = (counts[o.status] || 0) + 1;
    });
    return counts;
  }, [visibleOrders]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visibleOrders.filter(o => statusFilter === 'all' || o.status === statusFilter).filter(o => methodFilter === 'all' || o.method === methodFilter).filter(o => {
      if (!q) return true;
      return o.id.toLowerCase().includes(q) || o.customerName.toLowerCase().includes(q) || (o.customerPhone || '').toLowerCase().includes(q);
    }).sort((a, b) => b.placedAt - a.placedAt);
  }, [visibleOrders, statusFilter, methodFilter, search]);
  const selected = useMemo(() => orders.find(o => o.id === selectedId) || null, [orders, selectedId]);
  const pendingPayments = useMemo(() => visibleOrders.filter(o => o.paymentStatus === 'pending' && o.status !== 'cancelled' && o.status !== 'completed').length, [visibleOrders]);
  const handlePrint = (order: Order) => {
    markPrinted(order.id);
    // In a real app, this would trigger a thermal printer
    alert(`Printing ticket for ${order.id}...`);
  };
  const handleCancelConfirm = () => {
    if (!cancelModalOrderId || !cancelReason.trim()) return;
    const order = orders.find(o => o.id === cancelModalOrderId);
    if (!order) return;
    // Calculate waste from items that were already cooked (ready === true)
    const wasteValue = order.items.reduce((sum, item) => {
      if (item.ready) {
        const modsTotal = item.modifiers?.reduce((s, m) => s + m.price, 0) || 0;
        return sum + (item.unitPrice + modsTotal) * item.quantity;
      }
      return sum;
    }, 0);
    setStatus(cancelModalOrderId, 'cancelled', {
      reason: cancelReason.trim(),
      wasteValue,
      cancelledAt: Date.now()
    });
    setCancelModalOrderId(null);
    setCancelReason('');
  };
  return <div className="flex h-screen w-full bg-[#f9fafb] overflow-hidden font-sans flex-col md:flex-row" data-id="element-2609">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-[#0f172a] text-white p-4 flex-shrink-0 z-30" data-id="element-2610">
        <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-white/10 rounded-md transition-colors" data-id="element-2611">
          <MenuIcon size={24} data-id="element-2612" />
        </button>
        <span className="font-semibold" data-id="element-2613">Orders</span>
        <div className="w-6" data-id="element-2614" />
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-2615" />

      <main className="flex-1 flex flex-col h-full overflow-hidden" data-id="element-2616">
        {/* Top header */}
        <div className="px-4 md:px-6 pt-5 md:pt-6 pb-4 border-b border-gray-200 bg-white flex-shrink-0" data-id="element-2617">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4" data-id="element-2618">
            <div data-id="element-2619">
              <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1 flex items-center gap-1.5" data-id="element-2620">
                <Bell size={13} data-id="element-2621" />
                Live orders
              </div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3" data-id="element-2622">
                Orders
                {statusCounts.new > 0 && <motion.span initial={{
                scale: 0.85,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} className="inline-flex items-center gap-1.5 bg-blue-500 text-white text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-2623">
                    <span className="relative flex h-2 w-2" data-id="element-2624">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" data-id="element-2625" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-white" data-id="element-2626" />
                    </span>
                    {statusCounts.new} new
                  </motion.span>}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-2627">
                {orders.length} total orders
                {pendingPayments > 0 && <>
                    {' · '}
                    <span className="text-amber-600 font-semibold" data-id="element-2628">
                      {pendingPayments} pending payment
                      {pendingPayments === 1 ? '' : 's'}
                    </span>
                  </>}
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-2629">
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200" data-id="element-2630">
                <span className="relative flex h-2 w-2" data-id="element-2631">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" data-id="element-2632" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" data-id="element-2633" />
                </span>
                Auto-verify enabled
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3" data-id="element-2634">
            <div className="relative flex-1 max-w-md" data-id="element-2635">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-2636" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by order # or customer..." className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-2637" />
              {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-2638">
                  <X size={14} data-id="element-2639" />
                </button>}
            </div>

            {/* Status filter */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto" data-id="element-2640">
              {(['all', 'new', 'preparing', 'ready', 'completed', 'cancelled'] as StatusFilter[]).map(s => <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5 whitespace-nowrap ${statusFilter === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2641">
                  {s === 'all' ? 'All' : statusConfig[s as OrderStatus].label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusFilter === s ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-600'}`} data-id="element-2642">
                    {statusCounts[s]}
                  </span>
                </button>)}
            </div>

            {/* Method filter */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1" data-id="element-2643">
              {(['all', 'table', 'takeout', 'delivery', 'dineAhead'] as MethodFilter[]).map(m => <button key={m} onClick={() => setMethodFilter(m)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors whitespace-nowrap ${methodFilter === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2644">
                  {m === 'all' ? 'All methods' : methodConfig[m as OrderMethod].short}
                </button>)}
            </div>
          </div>
        </div>

        {/* Split content */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[400px_1fr]" data-id="element-2645">
          {/* List */}
          <div className="overflow-y-auto border-r border-gray-200 bg-gray-50/40" data-id="element-2646">
            <AnimatePresence mode="popLayout" data-id="element-2647">
              {filtered.map(order => <OrderListItem key={order.id} order={order} branchOutlets={branchOutlets} isSelected={selectedId === order.id} isRecentlyVerified={recentlyVerifiedId === order.id} onClick={() => setSelectedId(order.id)} onPrint={() => {
              handlePrint(order);
            }} data-id="element-2648" />)}
            </AnimatePresence>
            {filtered.length === 0 && <div className="p-10 text-center" data-id="element-2649">
                <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-2650">
                  <Search size={20} className="text-gray-400" data-id="element-2651" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-2652">
                  No orders match
                </h3>
                <p className="text-xs text-gray-500" data-id="element-2653">
                  Try adjusting your filters or search.
                </p>
              </div>}
          </div>

          {/* Detail */}
          <div className="overflow-y-auto bg-white" data-id="element-2654">
            {selected ? <OrderDetail order={selected} kdsEnabled={kdsEnabled} onAdvance={() => {
            // In counter-service mode the kitchen step doesn't exist,
            // so accepting a new order jumps straight to "ready" (the
            // food is effectively done) and the cashier just needs to
            // hand it over.
            if (!kdsEnabled && selected.status === 'new') {
              setStatus(selected.id, 'ready');
              return;
            }
            const next = nextStatus[selected.status];
            if (next) setStatus(selected.id, next);
          }} onComplete={() => setStatus(selected.id, 'completed')} onCancel={() => setCancelModalOrderId(selected.id)} onMarkPaid={() => {
            // For waiter-handoff (tab) orders, verifying payment is
            // also the close-out gesture — flip the order to completed
            // in the same action so the cashier doesn't have to
            // separately mark it "Handed to customer".
            if (selected.tabId) {
              updateOrder(selected.id, {
                status: 'completed'
              });
            }
            setPaymentStatus(selected.id, 'paid');
          }} onMarkPaidWithMethod={method => {
            updateOrder(selected.id, {
              paymentMethod: method,
              // Tab order? Close it out at the same time.
              ...(selected.tabId ? {
                status: 'completed' as const
              } : {})
            });
            setPaymentStatus(selected.id, 'paid');
          }} onRefund={() => setPaymentStatus(selected.id, 'refunded')} onPrint={() => handlePrint(selected)} isRecentlyVerified={recentlyVerifiedId === selected.id} data-id="element-2655" /> : <div className="h-full flex items-center justify-center p-10 text-center" data-id="element-2656">
                <div data-id="element-2657">
                  <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-2658">
                    <ShoppingCart size={22} className="text-gray-400" data-id="element-2659" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm mb-1" data-id="element-2660">
                    Select an order
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-2661">
                    Pick an order from the list to see its details.
                  </p>
                </div>
              </div>}
          </div>
        </div>
      </main>

      {/* Cancellation Modal */}
      {cancelModalOrderId && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-id="element-2662">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" data-id="element-2663">
            <div className="flex items-center gap-3 mb-4" data-id="element-2664">
              <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2665">
                <Trash2 size={24} data-id="element-2666" />
              </div>
              <div data-id="element-2667">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-2668">
                  Cancel Order
                </h2>
                <p className="text-sm text-gray-500" data-id="element-2669">{cancelModalOrderId}</p>
              </div>
            </div>

            {(() => {
          const order = orders.find(o => o.id === cancelModalOrderId);
          if (!order) return null;
          const cookedItems = order.items.filter(i => i.ready);
          const wasteValue = cookedItems.reduce((sum, item) => {
            const modsTotal = item.modifiers?.reduce((s, m) => s + m.price, 0) || 0;
            return sum + (item.unitPrice + modsTotal) * item.quantity;
          }, 0);
          return <div className="mb-6" data-id="element-2670">
                  {cookedItems.length > 0 && <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2" data-id="element-2671">
                      <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2672" />
                      <div className="text-sm text-amber-800" data-id="element-2673">
                        <span className="font-bold" data-id="element-2674">
                          Warning: Food already cooked.
                        </span>
                        <p className="mt-0.5 text-xs" data-id="element-2675">
                          {cookedItems.length} item(s) are already prepared.
                          Cancelling will log ETB {wasteValue.toFixed(2)} in
                          waste.
                        </p>
                      </div>
                    </div>}

                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-2676">
                    Reason for cancellation
                  </label>
                  <select value={cancelReason} onChange={e => setCancelReason(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 mb-3" data-id="element-2677">
                    <option value="" data-id="element-2678">Select a reason...</option>
                    <option value="Customer requested" data-id="element-2679">
                      Customer requested
                    </option>
                    <option value="Kitchen error / Remake" data-id="element-2680">
                      Kitchen error / Remake
                    </option>
                    <option value="Item out of stock" data-id="element-2681">Item out of stock</option>
                    <option value="Fake order / Spam" data-id="element-2682">Fake order / Spam</option>
                    <option value="Other" data-id="element-2683">Other</option>
                  </select>
                </div>;
        })()}

            <div className="flex gap-3" data-id="element-2684">
              <button onClick={() => {
            setCancelModalOrderId(null);
            setCancelReason('');
          }} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors" data-id="element-2685">
                Keep Order
              </button>
              <button onClick={handleCancelConfirm} disabled={!cancelReason} className="flex-1 py-3 rounded-xl bg-rose-600 text-white font-bold hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2686">
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>}
    </div>;
}
function OrderListItem({
  order,
  branchOutlets,
  isSelected,
  isRecentlyVerified,
  onClick,
  onPrint
}: {
  order: Order;
  branchOutlets: any[];
  isSelected: boolean;
  isRecentlyVerified: boolean;
  onClick: () => void;
  onPrint: () => void;
}) {
  const mc = methodConfig[order.method];
  const sc = statusConfig[order.status];
  const pc = paymentConfig[order.paymentStatus];
  const outlet = order.outletId ? branchOutlets.find(o => o.id === order.outletId) : null;
  const StatusIcon = sc.Icon;
  const MethodIcon = mc.Icon;
  return <motion.button layout initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -4
  }} onClick={onClick} className={`group w-full text-left px-4 py-3 border-b border-gray-100 transition-colors relative ${isSelected ? 'bg-orange-50/60 ring-1 ring-inset ring-orange-200' : 'hover:bg-white bg-transparent'}`} data-id="element-2687">
      {isRecentlyVerified && <motion.span initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider" data-id="element-2688">
          <Sparkles size={9} data-id="element-2689" />
          Verified
        </motion.span>}
      <div className="flex items-start justify-between gap-2 mb-1.5" data-id="element-2690">
        <div className="min-w-0 flex-1" data-id="element-2691">
          <div className="flex items-center gap-2 mb-0.5" data-id="element-2692">
            <span className="font-bold text-sm text-gray-900 truncate" data-id="element-2693">
              {order.id}
            </span>
            {order.isOpenTab && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700" data-id="element-2694">
                Open tab
              </span>}
            {order.splitOf && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600" data-id="element-2695">
                Split {order.splitOf.index}/{order.splitOf.total}
              </span>}
          </div>
          <div className="text-sm text-gray-700 truncate" data-id="element-2696">
            {order.customerName}
          </div>
        </div>
        <div className="text-right flex-shrink-0" data-id="element-2697">
          <div className="font-bold text-sm text-gray-900 tabular-nums" data-id="element-2698">
            ETB {order.total.toFixed(2)}
          </div>
          <div className="text-[10px] text-gray-500" data-id="element-2699">
            {timeSincePlaced(order.placedAt)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap" data-id="element-2700">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${sc.bg} ${sc.color}`} data-id="element-2701">
          {StatusIcon && <StatusIcon size={10} data-id="element-2702" />}
          {sc.label}
        </span>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${mc.bg} ${mc.color}`} data-id="element-2703">
          {MethodIcon && <MethodIcon size={10} data-id="element-2704" />}
          {mc.short}
          {order.tableNumber ? ` ${order.tableNumber}` : ''}
        </span>
        {outlet && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-${outlet.color}-100 text-${outlet.color}-700`} data-id="element-2705">
            {outlet.icon && <span data-id="element-2706">{outlet.icon}</span>}
            {outlet.name}
          </span>}
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${pc.bg} ${pc.color}`} data-id="element-2707">
          {pc.label}
        </span>
        <button type="button" onClick={e => {
        e.stopPropagation();
        onPrint();
      }} className="ml-auto p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity" title="Print ticket" data-id="element-2708">
          <Printer size={12} data-id="element-2709" />
        </button>
      </div>
    </motion.button>;
}
function OrderDetail({
  order,
  onAdvance,
  onComplete,
  onCancel,
  onMarkPaid,
  onMarkPaidWithMethod,
  onRefund,
  onPrint,
  isRecentlyVerified,
  kdsEnabled
}: {
  order: Order;
  onAdvance: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onMarkPaid: () => void;
  onMarkPaidWithMethod: (method: PaymentMethod) => void;
  onRefund: () => void;
  onPrint: () => void;
  isRecentlyVerified: boolean;
  kdsEnabled: boolean;
}) {
  const [showMethodPicker, setShowMethodPicker] = useState(false);
  const {
    branches
  } = useBranches();
  const {
    getOutletsForBranch
  } = useOutlets();
  const branchOutlets = getOutletsForBranch(order.branchId || '');
  const [receivedAmount, setReceivedAmount] = useState('');
  const isBankTransfer = order.paymentMethod === 'bank-transfer';
  const bankBranch = branches.find(b => b.id === orderBranchId(order));
  const bankAccount = bankBranch?.bankAccounts?.find(a => a.id === order.selectedBankAccountId);
  const recvNum = parseFloat(receivedAmount);
  const hasReceivedInput = receivedAmount.trim() !== '' && !isNaN(recvNum);
  const amountMatches = hasReceivedInput && Math.abs(recvNum - order.total) < 0.01;
  const canCancel = order.status !== 'completed' && order.status !== 'cancelled';
  const isPaid = order.paymentStatus === 'paid';
  const sc = statusConfig[order.status];
  const pc = paymentConfig[order.paymentStatus];
  const mc = methodConfig[order.method];
  const MethodIcon = (mc as any).Icon || (() => null);
  const PayIcon = order.paymentMethod === 'card' ? CreditCard : order.paymentMethod === 'cash' ? Banknote : Smartphone;
  const isFromTab = !!order.tabId;
  const advanceTarget = nextStatus[order.status];
  const canAdvance = advanceTarget !== null;
  const AdvanceIcon = advanceTarget === 'preparing' ? ChefHat : advanceTarget === 'ready' ? PackageCheck : CheckCircle2;
  const advanceLabel: Record<OrderStatus, string> = {
    new: 'Accept order',
    preparing: '',
    ready: 'Hand to customer',
    completed: '',
    cancelled: ''
  };
  return <>
      <div className="flex flex-col" data-id="element-2710">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex-shrink-0" data-id="element-2711">
          <div className="flex items-start justify-between mb-4" data-id="element-2712">
            <div data-id="element-2713">
              <div className="flex items-center gap-2 mb-1" data-id="element-2714">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-2715">{order.id}</h2>
                {order.splitOf && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600" data-id="element-2716">
                    Split {order.splitOf.index} of {order.splitOf.total}
                  </span>}
              </div>
              <h2 className="text-xl font-bold text-gray-900" data-id="element-2717">
                {order.customerName}
              </h2>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap" data-id="element-2718">
                <span className="flex items-center gap-1" data-id="element-2719">
                  <Phone size={11} data-id="element-2720" />
                  {order.customerPhone}
                </span>
                {order.printedAt && <span className="flex items-center gap-1 text-emerald-600 font-semibold" data-id="element-2721">
                    <Printer size={11} data-id="element-2722" />
                    Printed {timeSincePlaced(order.printedAt)}
                  </span>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2" data-id="element-2723">
              <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide ${sc.bg} ${sc.color}`} data-id="element-2724">
                {sc.label}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border ${mc.color} ${mc.bg}`} data-id="element-2725">
                <MethodIcon size={12} data-id="element-2726" />
                {order.tableNumber ? `Table ${order.tableNumber} · ${mc.label}` : mc.label}
              </span>
            </div>
          </div>
        </div>

        {/* Payment card */}
        <motion.div layout className={`relative rounded-xl border p-4 mb-5 ${pc.bg}`} data-id="element-2727">
          <div className="flex items-start justify-between gap-3 flex-wrap" data-id="element-2728">
            <div className="flex items-center gap-3" data-id="element-2729">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-white shadow-sm ${pc.color}`} data-id="element-2730">
                <PayIcon size={18} data-id="element-2731" />
              </div>
              <div data-id="element-2732">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500" data-id="element-2733">
                  Payment
                </div>
                <div className="flex items-center gap-2 mt-0.5" data-id="element-2734">
                  <span className={`font-bold ${pc.color}`} data-id="element-2735">{pc.label}</span>
                  <span className="text-gray-300" data-id="element-2736">·</span>
                  <span className="text-sm text-gray-700 font-medium" data-id="element-2737">
                    {order.paymentMethod ? paymentMethodLabel[order.paymentMethod] : 'Method not set'}
                  </span>
                </div>
                {order.paymentVerifiedAt && order.paymentStatus === 'paid' && <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1" data-id="element-2738">
                    <ShieldCheck size={11} className="text-emerald-600" data-id="element-2739" />
                    Verified {timeSincePlaced(order.paymentVerifiedAt)}
                  </div>}
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-2740">
              {order.paymentStatus === 'pending' && order.paymentMethod && !isBankTransfer && <button onClick={onMarkPaid} className="px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors flex items-center gap-1.5 shadow-sm" data-id="element-2741">
                    <Check size={13} data-id="element-2742" />
                    Verify payment
                  </button>}
              {order.paymentStatus === 'pending' && !order.paymentMethod && <>
                  <button onClick={() => setShowMethodPicker(v => !v)} className="px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors flex items-center gap-1.5 shadow-sm" data-id="element-2743">
                    <Check size={13} data-id="element-2744" />
                    {order.tabId ? 'Verify & close as…' : 'Verify payment as…'}
                  </button>
                  {showMethodPicker && <div className="absolute right-0 top-full mt-2 z-20 w-56 rounded-xl bg-white border border-gray-200 shadow-lg overflow-hidden" data-id="element-2745">
                      <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500 border-b border-gray-100 bg-gray-50" data-id="element-2746">
                        {order.tabId ? 'Close out — paid with' : 'Customer paid with'}
                      </div>
                      {(['cash', 'card', 'telebirr', 'cbe-birr'] as const).map(m => <button key={m} onClick={() => {
                  onMarkPaidWithMethod(m);
                  setShowMethodPicker(false);
                }} className="w-full text-left px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 transition-colors" data-id="element-2747">
                            {paymentMethodLabel[m]}
                          </button>)}
                    </div>}
                </>}
              {order.paymentStatus === 'paid' && order.status === 'cancelled' && <button onClick={onRefund} className="px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-1.5" data-id="element-2748">
                    <RefreshCw size={13} data-id="element-2749" />
                    Mark refunded
                  </button>}
            </div>
          </div>
          <AnimatePresence data-id="element-2750">
            {isRecentlyVerified && <motion.div initial={{
            opacity: 0,
            y: -4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} className="absolute -top-2 right-3 bg-emerald-500 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-md" data-id="element-2751">
                <Sparkles size={10} data-id="element-2752" />
                Just auto-verified
              </motion.div>}
          </AnimatePresence>
        </motion.div>

        {/* Bank transfer verification (CBE share-link layered verifier) */}
        {isBankTransfer && order.paymentStatus === 'pending' && <CBETransferVerifier order={order} onApprove={onMarkPaid} data-id="element-2753" />}
        {false && isBankTransfer && order.paymentStatus === 'pending' && <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 mb-5" data-id="element-2754">
            <div className="flex items-center gap-2 mb-3" data-id="element-2755">
              <div className="w-8 h-8 rounded-lg bg-white shadow-sm text-indigo-600 flex items-center justify-center" data-id="element-2756">
                <Building2 size={16} data-id="element-2757" />
              </div>
              <div data-id="element-2758">
                <div className="text-[11px] font-bold uppercase tracking-wide text-indigo-700" data-id="element-2759">
                  Bank transfer · awaiting verification
                </div>
                <div className="text-[11px] text-indigo-600/80" data-id="element-2760">
                  Check your banking app, then confirm receipt.
                </div>
              </div>
            </div>

            <div className="space-y-2.5 bg-white rounded-lg border border-indigo-100 p-3" data-id="element-2761">
              <div className="flex items-center justify-between gap-3" data-id="element-2762">
                <span className="text-xs text-gray-500" data-id="element-2763">Expected amount</span>
                <span className="text-sm font-extrabold text-gray-900 tabular-nums" data-id="element-2764">
                  ETB {order.total.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3" data-id="element-2765">
                <span className="text-xs text-gray-500" data-id="element-2766">Order reference</span>
                <span className="text-sm font-bold text-gray-900" data-id="element-2767">
                  {order.id}
                </span>
              </div>
              {bankAccount ? <div className="pt-1.5 border-t border-gray-100" data-id="element-2768">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1" data-id="element-2769">
                    Paid into
                  </div>
                  <div className="text-sm font-semibold text-gray-900" data-id="element-2770">
                    {bankAccount.bankName}
                    {bankAccount.label ? ` · ${bankAccount.label}` : ''}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5" data-id="element-2771">
                    <span className="text-sm text-gray-700 tabular-nums" data-id="element-2772">
                      {bankAccount.accountNumber}
                    </span>
                    <button onClick={() => navigator.clipboard?.writeText(bankAccount.accountNumber)} className="text-gray-400 hover:text-gray-700 p-1" aria-label="Copy account number" data-id="element-2773">
                      <Copy size={13} data-id="element-2774" />
                    </button>
                  </div>
                </div> : null}
              <div className="pt-1.5 border-t border-gray-100" data-id="element-2775">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1" data-id="element-2776">
                  Customer's transfer reference
                </div>
                {order.bankTransferRef ? <div className="text-sm font-semibold text-gray-900 break-all" data-id="element-2777">
                    {order.bankTransferRef}
                  </div> : <div className="text-sm text-amber-600 font-medium flex items-center gap-1.5" data-id="element-2778">
                    <AlertTriangle size={13} data-id="element-2779" />
                    Not provided by customer
                  </div>}
              </div>
            </div>

            {/* Amount confirmation aid */}
            <div className="mt-3" data-id="element-2780">
              <label className="block text-[11px] font-semibold text-indigo-800 mb-1.5" data-id="element-2781">
                Amount received{' '}
                <span className="font-normal text-indigo-500" data-id="element-2782">(optional)</span>
              </label>
              <div className="relative" data-id="element-2783">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400" data-id="element-2784">
                  ETB
                </span>
                <input type="number" min={0} value={receivedAmount} onChange={e => setReceivedAmount(e.target.value)} placeholder={order.total.toString()} className="w-full pl-12 pr-3 py-2.5 rounded-lg border border-indigo-200 text-sm font-bold tabular-nums bg-white focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" data-id="element-2785" />
              </div>
              {hasReceivedInput && (amountMatches ? <div className="text-[11px] text-emerald-600 font-semibold mt-1.5 flex items-center gap-1" data-id="element-2786">
                    <CheckCircle2 size={12} data-id="element-2787" />
                    Matches the expected amount.
                  </div> : <div className="text-[11px] text-amber-600 font-semibold mt-1.5 flex items-center gap-1" data-id="element-2788">
                    <AlertTriangle size={12} data-id="element-2789" />
                    {recvNum > order.total ? 'Overpaid' : 'Underpaid'} by ETB{' '}
                    {Math.abs(recvNum - order.total).toLocaleString()} vs
                    expected.
                  </div>)}
            </div>

            <button onClick={onMarkPaid} className="w-full mt-3 px-3 py-2.5 rounded-lg text-sm font-bold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-2790">
              <ShieldCheck size={15} data-id="element-2791" />
              Mark payment received
            </button>
          </div>}

        {/* Outlet badge */}
        {(() => {
        const outlet = order.outletId ? branchOutlets.find(o => o.id === order.outletId) : null;
        if (!outlet) return null;
        return <div className={`bg-${outlet.color}-50 border border-${outlet.color}-200 rounded-xl p-3 mb-5 flex gap-2.5`} data-id="element-2792">
              <div className={`text-${outlet.color}-600 flex-shrink-0 mt-0.5`} data-id="element-2793">
                {outlet.icon && <span data-id="element-2794">{outlet.icon}</span>}
              </div>
              <div className="flex-1 min-w-0" data-id="element-2795">
                <div className={`text-[11px] font-bold uppercase tracking-wide text-${outlet.color}-700 mb-0.5`} data-id="element-2796">
                  Outlet
                </div>
                <p className={`text-sm text-${outlet.color}-900 font-medium`} data-id="element-2797">
                  {outlet.name}
                </p>
              </div>
            </div>;
      })()}

        {/* Delivery address */}
        {order.method === 'delivery' && order.deliveryAddress && <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-5 flex gap-2.5" data-id="element-2798">
            <Bike size={15} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-2799" />
            <div className="flex-1 min-w-0" data-id="element-2800">
              <div className="text-[11px] font-bold uppercase tracking-wide text-blue-700 mb-0.5" data-id="element-2801">
                Delivery address
              </div>
              <p className="text-sm text-blue-900 font-medium" data-id="element-2802">
                {order.deliveryAddress}
              </p>
            </div>
          </div>}

        {/* Customer notes */}
        {order.customerNotes && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 flex gap-2.5" data-id="element-2803">
            <MessageSquare size={15} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2804" />
            <div data-id="element-2805">
              <div className="text-[11px] font-bold uppercase tracking-wide mb-0.5" data-id="element-2806">
                Customer note
              </div>
              <p className="text-sm text-amber-900" data-id="element-2807">{order.customerNotes}</p>
            </div>
          </div>}

        {/* Custom Question Answers */}
        {order.answers && order.answers.length > 0 && <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-5" data-id="element-2808">
            <div className="text-[11px] font-bold uppercase tracking-wide text-purple-700 mb-2" data-id="element-2809">
              Customer Responses
            </div>
            <div className="space-y-3" data-id="element-2810">
              {order.answers.map((ans, i) => <div key={i} data-id="element-2811">
                  <div className="text-xs font-semibold text-purple-900 mb-0.5" data-id="element-2812">
                    {ans.question}
                  </div>
                  <div className="text-sm text-purple-800" data-id="element-2813">
                    {Array.isArray(ans.answer) ? ans.answer.join(', ') : ans.answer}
                  </div>
                </div>)}
            </div>
          </div>}

        {/* Items */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-5" data-id="element-2814">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" data-id="element-2815">
            <div className="text-xs font-bold uppercase tracking-wide text-gray-500" data-id="element-2816">
              Items ({order.items.reduce((s, i) => s + i.quantity, 0)})
            </div>
          </div>
          <div className="divide-y divide-gray-100" data-id="element-2817">
            {order.items.map(item => {
            const modTotal = (item.modifiers || []).reduce((s, m) => s + m.price, 0);
            const lineTotal = (item.unitPrice + modTotal) * item.quantity;
            return <div key={item.id} className="px-4 py-3 flex items-start justify-between gap-3" data-id="element-2818">
                  <div className="flex-1 min-w-0" data-id="element-2819">
                    <div className="flex items-baseline gap-2" data-id="element-2820">
                      <span className="text-[13px] font-bold text-gray-500 tabular-nums" data-id="element-2821">
                        ×{item.quantity}
                      </span>
                      <span className="text-sm font-semibold text-gray-900" data-id="element-2822">
                        {item.name}
                      </span>
                      {item.variation && <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-100" data-id="element-2823">
                          {item.variation}
                        </span>}
                    </div>
                    {item.modifiers && item.modifiers.length > 0 && <ul className="mt-1 ml-7 space-y-0.5" data-id="element-2824">
                        {item.modifiers.map((m, i) => <li key={i} className="text-[12px] text-gray-500 flex items-center gap-1" data-id="element-2825">
                            <span className="text-gray-300" data-id="element-2826">+</span>
                            {m.name}
                            {m.price > 0 && <span className="text-gray-400 ml-1" data-id="element-2827">
                                (+ETB {m.price.toFixed(2)})
                              </span>}
                          </li>)}
                      </ul>}
                    {item.notes && <div className="mt-1 ml-7 text-[12px] italic text-gray-500" data-id="element-2828">
                        "{item.notes}"
                      </div>}
                    {/* Per-line delivery audit chip — only meaningful for tab-originated orders */}
                    {isFromTab && <div className="mt-1.5 ml-7" data-id="element-2829">
                        {item.deliveredAt ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100" data-id="element-2830">
                            <CheckCircle2 size={9} data-id="element-2831" />
                            Delivered{' '}
                            {new Date(item.deliveredAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    })}
                            {item.deliveredBy && ` · waiter ${item.deliveredBy}`}
                          </span> : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" data-id="element-2832">
                            <AlertCircle size={9} data-id="element-2833" />
                            Not delivery-confirmed
                          </span>}
                      </div>}
                  </div>
                  <div className="text-sm font-bold text-gray-900 tabular-nums flex-shrink-0" data-id="element-2834">
                    ETB {lineTotal.toFixed(2)}
                  </div>
                </div>;
          })}
          </div>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-1.5 text-sm" data-id="element-2835">
            <div className="flex items-center justify-between text-gray-600" data-id="element-2836">
              <span data-id="element-2837">Subtotal</span>
              <span className="tabular-nums" data-id="element-2838">
                ETB {order.subtotal.toFixed(2)}
              </span>
            </div>
            {order.packagingFee > 0 && <div className="flex items-center justify-between text-gray-600" data-id="element-2839">
                <span data-id="element-2840">Packaging</span>
                <span className="tabular-nums" data-id="element-2841">
                  ETB {order.packagingFee.toFixed(2)}
                </span>
              </div>}
            <div className="flex items-center justify-between text-gray-600" data-id="element-2842">
              <span data-id="element-2843">Service fee (5%)</span>
              <span className="tabular-nums" data-id="element-2844">
                ETB {order.serviceFee.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between pt-1.5 border-t border-gray-200 mt-1.5" data-id="element-2845">
              <span className="font-bold text-gray-900" data-id="element-2846">Total</span>
              <span className="font-bold text-gray-900 tabular-nums text-base" data-id="element-2847">
                ETB {order.total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        {order.status !== 'cancelled' && <div className="p-6 border-b border-gray-100 flex-shrink-0" data-id="element-2848">
            <StatusTimeline status={order.status} kdsEnabled={kdsEnabled} data-id="element-2849" />
          </div>}

        {order.status === 'ready' && kdsEnabled && !isFromTab && <button onClick={onComplete} className="w-full py-3.5 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2" data-id="element-2850">
            <CheckCircle2 size={20} data-id="element-2851" />
            Handed to Customer
          </button>}

        {/* Counter Service (no KDS): split into two atomic moments —
            Accept on arrival, then Complete on handoff. */}
        {!kdsEnabled && !isFromTab && order.status === 'new' && <div className="grid grid-cols-2 gap-3" data-id="element-2852">
            <button onClick={onAdvance} disabled={order.paymentStatus === 'pending'} className="flex-1 px-5 py-3 rounded-xl text-sm font-bold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2853">
              <AdvanceIcon size={16} data-id="element-2854" />
              Accept order
              {order.paymentStatus === 'pending' && <span className="text-[11px] font-normal opacity-90" data-id="element-2855">
                  · Awaiting payment
                </span>}
            </button>
            <button onClick={onPrint} className="px-5 py-3 rounded-xl text-sm font-bold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2" title="Print receipt" data-id="element-2856">
              <Printer size={16} data-id="element-2857" />
              {order.printedAt ? 'Re-print receipt' : 'Print receipt'}
            </button>
          </div>}

        {!kdsEnabled && !isFromTab && order.status !== 'new' && order.status !== 'completed' && order.status !== 'cancelled' && <button onClick={onComplete} className="w-full py-3.5 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2" data-id="element-2858">
              <CheckCircle2 size={20} data-id="element-2859" />
              Complete order
            </button>}

        {isFromTab && order.status !== 'cancelled' && <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2.5 text-xs text-slate-600" data-id="element-2860">
            <ChefHat size={14} className="text-slate-500 flex-shrink-0" data-id="element-2861" />
            <span data-id="element-2862">
              <span className="font-semibold text-slate-700" data-id="element-2863">
                Waiter-served.
              </span>{' '}
              Food was delivered to the table. This view is for payment and
              audit only.
            </span>
          </div>}

        {/* Cashier "Accept order" — only on new orders when KDS is on */}
        {order.status === 'new' && kdsEnabled && !isFromTab && <div className="grid grid-cols-2 gap-3" data-id="element-2864">
            <button onClick={onAdvance} disabled={order.paymentStatus === 'pending'} className="flex-1 px-5 py-3 rounded-xl text-sm font-bold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2865">
              <AdvanceIcon size={16} data-id="element-2866" />
              Accept order
              {order.paymentStatus === 'pending' && <span className="text-[11px] font-normal opacity-90" data-id="element-2867">
                  · Awaiting payment
                </span>}
            </button>
            <button onClick={onPrint} className="px-5 py-3 rounded-xl text-sm font-bold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2" title="Print kitchen ticket" data-id="element-2868">
              <Printer size={16} data-id="element-2869" />
              {order.printedAt ? 'Re-print ticket' : 'Print kitchen ticket'}
            </button>
          </div>}

        {/* Read-only "kitchen is preparing" — owned by kitchen, cashier waits */}
        {order.status === 'preparing' && kdsEnabled && !isFromTab && <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 flex items-start gap-3" data-id="element-2870">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-700" data-id="element-2871">
              <ChefHat size={16} data-id="element-2872" />
            </div>
            <div className="flex-1" data-id="element-2873">
              <h3 className="text-sm font-semibold text-amber-900" data-id="element-2874">
                Kitchen is preparing
              </h3>
              <p className="text-xs text-amber-800 mt-0.5" data-id="element-2875">
                The kitchen will mark items ready as they come up. This order
                will move to your "Ready" queue automatically.
              </p>
            </div>
          </div>}

        {order.status !== 'cancelled' && !(order.status === 'new' && !isFromTab) && <button onClick={onPrint} className="w-full px-5 py-3 rounded-xl text-sm font-bold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2" title={isFromTab ? 'Re-print receipt' : 'Print receipt'} data-id="element-2876">
              <Printer size={16} data-id="element-2877" />
              {isFromTab ? order.printedAt ? 'Re-print receipt' : 'Print receipt' : order.printedAt ? 'Re-print receipt' : 'Print receipt'}
            </button>}

        {canCancel && <button onClick={onCancel} className="px-5 py-3 rounded-xl text-sm font-bold border border-red-200 text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center gap-2" data-id="element-2878">
            <X size={16} data-id="element-2879" />
            Cancel order
          </button>}
      </div>
    </>;
}
const TIMELINE_STEPS: {
  key: OrderStatus;
  label: string;
}[] = [{
  key: 'new',
  label: 'Placed'
}, {
  key: 'preparing',
  label: 'Preparing'
}, {
  key: 'ready',
  label: 'Ready'
}, {
  key: 'completed',
  label: 'Completed'
}];
function StatusTimeline({
  status,
  kdsEnabled
}: {
  status: OrderStatus;
  kdsEnabled: boolean;
}) {
  if (status === 'cancelled') {
    return <>
        <div className="bg-red-50 border border-red-100 rounded-xl p-3 flex items-center gap-2.5" data-id="element-2880">
          <XCircle size={16} className="text-red-600" data-id="element-2881" />
          <span className="text-sm font-semibold text-red-700" data-id="element-2882">
            Order cancelled
          </span>
        </div>
      </>;
  }
  if (!kdsEnabled) {
    const isCompleted = status === 'completed';
    return <div className="bg-gray-50 border border-gray-100 rounded-xl p-4" data-id="element-2883">
        <div className="flex items-center justify-between relative px-8" data-id="element-2884">
          <div className="absolute left-12 right-12 top-3 h-0.5 bg-gray-200" data-id="element-2885" />
          <motion.div className="absolute left-12 top-3 h-0.5 bg-orange-500" initial={{
          width: 0
        }} animate={{
          width: isCompleted ? 'calc(100% - 96px)' : 0
        }} transition={{
          duration: 0.5,
          ease: 'easeOut'
        }} data-id="element-2886" />

          <div className="flex flex-col items-center gap-1.5 relative z-10" data-id="element-2887">
            <div className="w-6 h-6 rounded-full flex items-center justify-center border-2 bg-orange-500 border-orange-500 text-white" data-id="element-2888">
              <Check size={12} strokeWidth={3} data-id="element-2889" />
            </div>
            <span className="text-[10px] font-semibold text-gray-900" data-id="element-2890">
              Order placed
            </span>
          </div>

          <div className="flex flex-col items-center gap-1.5 relative z-10" data-id="element-2891">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${isCompleted ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-gray-300 text-gray-400'}`} data-id="element-2892">
              {isCompleted ? <Check size={12} strokeWidth={3} data-id="element-2893" /> : <span className="text-[10px] font-bold" data-id="element-2894">2</span>}
            </div>
            <span className={`text-[10px] font-semibold ${isCompleted ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-2895">
              Completed
            </span>
          </div>
        </div>
      </div>;
  }
  const currentIdx = TIMELINE_STEPS.findIndex(s => s.key === status);
  return <div className="bg-gray-50 border border-gray-100 rounded-xl p-4" data-id="element-2896">
      <div className="flex items-center justify-between relative" data-id="element-2897">
        {/* Connector line */}
        <div className="absolute left-3 right-3 top-3 h-0.5 bg-gray-200" data-id="element-2898" />
        <motion.div className="absolute left-3 top-3 h-0.5 bg-orange-500" initial={{
        width: 0
      }} animate={{
        width: currentIdx <= 0 ? 0 : `calc(${currentIdx / (TIMELINE_STEPS.length - 1) * 100}% - 24px)`
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-2899" />
        {TIMELINE_STEPS.map((step, i) => {
        const done = i <= currentIdx;
        const isCurrent = i === currentIdx;
        return <>
              <div key={step.key} className="flex flex-col items-center gap-1.5 relative z-10" data-id="element-2900">
                <motion.div animate={{
              scale: isCurrent ? 1.1 : 1
            }} className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${done ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-gray-300 text-gray-400'} ${isCurrent ? 'ring-4 ring-orange-200' : ''}`} data-id="element-2901">
                  {done ? <Check size={12} strokeWidth={3} data-id="element-2902" /> : <span className="text-[10px] font-bold" data-id="element-2903">{i + 1}</span>}
                </motion.div>
                <span className={`text-[10px] font-semibold ${done ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-2904">
                  {step.label}
                </span>
              </div>
            </>;
      })}
      </div>
    </div>;
}